import {ChangeDetectionStrategy, Component} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {BalanceSelectors} from '@store/balance/balance.selectors';
import {createSelector} from '@ngrx/store';
import {TableDefinition} from '@store/aviary/aviary.adapter';
import {PatronBalanceTransaction} from '@store/balance/balance.types';
import {ExpandedTransactionOpacNoteComponent} from '@app/my-account/balance/expansion-rows/expanded-transaction-opac-note.component';
import {
  getTransactionsTableColumnsDefinition,
  TRANSACTIONS_TABLE_EXPANSION_COLUMNS,
  TRANSACTIONS_TABLE_RESPONSIVE_DEFINITION
} from '@app/my-account/balance/balance.constants';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {createStringSelector} from '@aviary/store/store.helpers';
import {AccountBalanceComponent} from '@app/my-account/balance/account-balance/account-balance.component';
import {TableComponent} from '@aviary/components/table/table.component';
import {PatronActions} from '@store/patron/patron.actions';

@Component({
  selector: 'px-dependent-balance',
  template: `
    <div class="flex flex-col gap-y-7 px-0 md:px-6 py-4">
      <px-account-balance [forDependent]="true"/>

      <div>
        <div class="text-headline-medium">Account Activity</div>
        <mx-table [tableDefinition]="tableDefinition"/>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, AccountBalanceComponent, TableComponent]
})
export class DependentBalanceComponent {
  private readonly selectors = BalanceSelectors.DependentBalanceTransactions;

  public readonly tableDefinition: TableDefinition<PatronBalanceTransaction, typeof this.selectors> = {
    defaultSelectorContainer: this.selectors,
    dataSource: 'transactions',
    rowExpansion: {
      rowBased: {
        showIf: this.selectors.showDetailsExpansionFor,
        componentClass: ExpandedTransactionOpacNoteComponent
      },
      columnBased: {
        showIf: this.selectors.showAmountsExpansionFor,
        columns: TRANSACTIONS_TABLE_EXPANSION_COLUMNS
      }
    },
    responsiveColumnsDefinition: TRANSACTIONS_TABLE_RESPONSIVE_DEFINITION,
    columns: getTransactionsTableColumnsDefinition(this.selectors),
    hiddenDataIndicatorRow: {
      showIf: PaginationSelectors.onFinalPage,
      message: createStringSelector(
        this.selectors.includeAllTransactions,
        includeAll => includeAll ? 'End of all activity.' : 'Older activity may be hidden.'
      ),
      actionButtonLabel: createSelector<object, boolean, string>(
        this.selectors.includeAllTransactions,
        includeAll => includeAll ? 'Hide Older Activity' : 'Show All'
      ),
      clickActionCreator: PatronActions.toggleIncludeAllDependentBalanceTransactions
    },
    noDataRowMessage: 'No recent transactions.'
  };
}
